import * as _ from 'lodash';
import { AppendedSection } from '../../clients/spark/scenes/dashboard/ApplicationManagerContent';
import TemplateSectionStatusModel from '../models/templating/TemplateSectionStatusModel';
import TemplateFormFieldValueModel from '../models/templating/api/TemplateFormFieldValueModel';
import AppliedApplicationTemplateContainer from '../state/containers/AppliedApplicationTemplateContainer';
import TemplateFormFieldModel from '../models/templating/api/TemplateFormFieldModel';

export interface CombinedSection {
    sectionType: 'auto-generate' | 'template' | 'appended';
    appliedTemplateId?: string;
    section?: any;
    name?: string;
    id?: string;
    isComplete: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    priority?: number;
}

export interface SectionCombinationOptions {
    sections: TemplateSectionStatusModel[] | any[];
    appendedSections?: AppendedSection[];
    showAdminFields: boolean;
    importedFormFields?: TemplateFormFieldValueModel[];
    appliedAppTemplateContainer?: AppliedApplicationTemplateContainer;
}

/**
 * Helper function to check if a field is an admin field
 */
export const isAdminField = (field: TemplateFormFieldModel): boolean => {
    return field.title?.toLowerCase().startsWith('admin') ?? false;
};

/**
 * Helper function to check if a field's value matches its associated variable
 */
export const fieldValueMatchesAssociatedVariable = (
    field: TemplateFormFieldModel,
    fieldValue: string | undefined
): boolean => {
    return !!(field.associatedVariable && field.associatedVariable === fieldValue);
};

/**
 * Helper function to check if a field should be visible based on all filtering criteria
 * @param field - The field to check
 * @param fieldValue - The current value of the field
 * @param showAdminFields - Whether admin fields should be shown
 * @returns true if the field should be visible, false otherwise
 */
export const isFieldVisible = (
    field: TemplateFormFieldModel,
    fieldValue: string | undefined,
    showAdminFields: boolean = true
): boolean => {
    // Hide admin fields if showAdminFields is false
    if (!showAdminFields && isAdminField(field)) {
        return false;
    }

    // Hide field if it has an associatedVariable and the value matches it
    if (fieldValueMatchesAssociatedVariable(field, fieldValue) && (fieldValue !== field?.defaultValue)) {
        return false;
    }

    return true;
};

/**
 * Helper function to get the field value from template form field values
 */
export const getFieldValue = (
    field: TemplateFormFieldModel,
    templateFormFieldValues?: TemplateFormFieldValueModel[]
): string | undefined => {
    return templateFormFieldValues?.find(
        v => (field?.name && v?.dynamicFormFieldName === field?.name) ||
            (field?.id && v?.templateFormFieldId === field?.id)
    )?.value;
};

/**
 * Helper function to get visible fields for a section
 * @param section - The section containing the fields
 * @param showAdminFields - Whether admin fields should be shown
 * @param templateFormFieldValues - The current values of the fields
 * @returns Array of visible fields
 */
export const getVisibleFields = (
    section: any,
    showAdminFields: boolean,
    templateFormFieldValues?: TemplateFormFieldValueModel[]
): TemplateFormFieldModel[] => {
    const fields = section?.templateFormFields ?? section?.dynamicFormFields ?? [];

    return fields.filter((field: TemplateFormFieldModel) => {
        const fieldValue = getFieldValue(field, templateFormFieldValues);
        return isFieldVisible(field, fieldValue, showAdminFields);
    });
};

/**
 * Helper function to check if a section has any visible fields
 * @param section - The section to check
 * @param showAdminFields - Whether admin fields should be shown
 * @param templateFormFieldValues - The current values of the fields
 * @returns true if the section has at least one visible field
 */
export const sectionHasVisibleFields = (
    section: any,
    showAdminFields: boolean,
    templateFormFieldValues?: TemplateFormFieldValueModel[]
): boolean => {
    const visibleFields = getVisibleFields(section, showAdminFields, templateFormFieldValues);
    return visibleFields.length > 0;
};

/**
 * Creates a unified array of all sections (template, auto-generate, appended)
 * with consistent filtering and ordering logic
 */
export const combineSections = (options: SectionCombinationOptions): CombinedSection[] => {
    const { sections, appendedSections, showAdminFields, importedFormFields, appliedAppTemplateContainer } = options;

    // Sort template sections by priority
    const sortedTemplateSections = _.sortBy(sections ?? [], s => s?.section?.priority);

    // Get template form field values for visibility checking
    const getTemplateFormFieldValues = (section: any): TemplateFormFieldValueModel[] => {
        if (!appliedAppTemplateContainer || !section.appliedTemplateId) {
            return [];
        }
        const appliedTemplate = appliedAppTemplateContainer.state.appliedTemplates?.find(
            at => at.id === section.appliedTemplateId
        );
        return appliedTemplate?.templateFormFieldValues ?? [];
    };

    // Filter out admin sections if needed and sections with no visible fields
    const filteredTemplateSections = sortedTemplateSections.filter(section => {
        // Filter admin sections
        if (!showAdminFields && section.section?.title?.toLowerCase().startsWith('admin')) {
            return false;
        }

        // Filter sections with no visible fields
        const templateFormFieldValues = getTemplateFormFieldValues(section);
        return sectionHasVisibleFields(section.section, showAdminFields, templateFormFieldValues);
    });

    const combined: CombinedSection[] = [];

    // Add auto-generate section if it exists (always first)
    const autoGenerateSection = appendedSections?.find(section => section.name === "Auto-Generate Responses");
    if (autoGenerateSection) {
        combined.push({
            ...autoGenerateSection,
            sectionType: 'auto-generate',
            isComplete: autoGenerateSection.isComplete,
            isSelected: autoGenerateSection.isSelected
        });
    }

    // Add filtered template sections
    filteredTemplateSections.forEach(section => {
        const isComplete = getCompletionStatus(section, importedFormFields, appliedAppTemplateContainer);
        combined.push({
            ...section,
            sectionType: 'template',
            isComplete,
            priority: section.section?.priority
        });
    });

    // Add other appended sections (excluding auto-generate)
    const otherAppendedSections = appendedSections?.filter(section => section.name !== "Auto-Generate Responses");
    if (otherAppendedSections) {
        otherAppendedSections.forEach(section => {
            combined.push({
                ...section,
                sectionType: 'appended',
                isComplete: section.isComplete,
                isSelected: section.isSelected
            });
        });
    }

    return combined;
};

/**
 * Helper function to determine completion status of a section
 * Always uses the container's getIsSectionOfCurrentComplete method for consistency
 */
export const getCompletionStatus = (
    section: TemplateSectionStatusModel | any,
    importedFormFields?: TemplateFormFieldValueModel[],
    appliedAppTemplateContainer?: AppliedApplicationTemplateContainer
): boolean => {
    if (!appliedAppTemplateContainer) {
        return section.isComplete ?? false;
    }

    // Always use the container's standard completion check for consistency
    const appliedTemplate = appliedAppTemplateContainer.state.appliedTemplates?.find(
        at => at.id === (section as any).appliedTemplateId
    );

    if (!appliedTemplate) {
        return section.isComplete ?? false;
    }

    return appliedAppTemplateContainer.getIsSectionOfCurrentComplete(appliedTemplate, section.section);
};

/**
 * Navigates to the next section in a unified sections array
 * Sections with no visible fields are already filtered out by combineSections
 */
export const getNextSection = (
    allSections: CombinedSection[],
    currentIndex: number
): CombinedSection | 'finish' | null => {
    if (currentIndex === -1) {
        // No current section, go to first section
        return allSections.length > 0 ? allSections[0] : 'finish';
    } else if (currentIndex >= allSections.length - 1) {
        // Last section, go to finish
        return 'finish';
    } else {
        // Navigate to next section
        return allSections[currentIndex + 1];
    }
};

/**
 * Navigates to the previous section in a unified sections array
 * Sections with no visible fields are already filtered out by combineSections
 */
export const getPreviousSection = (
    allSections: CombinedSection[],
    currentIndex: number,
    isFinishSelected: boolean
): CombinedSection | 'start' | null => {
    if (currentIndex <= 0) {
        // First section or no section, go to start
        return 'start';
    } else if (currentIndex === -1 && isFinishSelected) {
        // Coming back from finish section
        return allSections.length > 0 ? allSections[allSections.length - 1] : 'start';
    } else {
        // Navigate to previous section
        return allSections[currentIndex - 1];
    }
};

/**
 * Finds the active section index in a combined sections array
 */
export const findActiveSectionIndex = (
    allSections: CombinedSection[],
    activeSection: any
): number => {
    return allSections.findIndex(s => {
        if (s.sectionType === 'template') {
            // Handle both direct section objects and currentSection objects with appliedTemplateId
            if (activeSection?.appliedTemplateId && activeSection?.section) {
                // This is a currentSection object from ApplicationManagerContent
                // Match by appliedTemplateId AND section properties to ensure uniqueness
                return activeSection.appliedTemplateId === s.appliedTemplateId &&
                    ((activeSection.section?.name && s?.section?.name === activeSection.section?.name) ||
                        (activeSection.section?.id && s.section?.id === activeSection.section?.id) ||
                        (activeSection.section?.title && s?.section?.title === activeSection.section?.title));
            } else {
                // This is a direct section object - still check appliedTemplateId if available
                const activeSectionDetails = activeSection?.section ?? activeSection;
                const sameTemplate = !activeSection?.appliedTemplateId || activeSection.appliedTemplateId === s.appliedTemplateId;
                if (!sameTemplate) {
                    return false;
                }
                return (activeSectionDetails?.name && s?.section?.name === activeSectionDetails.name) ||
                    (activeSectionDetails?.id && s.section?.id === activeSectionDetails.id) ||
                    (activeSectionDetails?.title && s?.section?.title === activeSectionDetails.title);
            }
        } else {
            // For appended sections, check if it's the same object or has matching properties
            return activeSection === s ||
                (activeSection?.name && s?.name === activeSection?.name) ||
                (activeSection?.id && s?.id === activeSection?.id);
        }
    });
};

/**
 * Gets the page count including the Review section
 */
export const getPageCount = (allSections: CombinedSection[]): number => {
    // Add 1 for the Review section
    return allSections.length + 1;
};

/**
 * Gets the current page number (1-indexed)
 */
export const getCurrentPageNumber = (
    allSections: CombinedSection[],
    currentIndex: number,
    isFinishSelected: boolean
): number => {
    if (isFinishSelected) return getPageCount(allSections);
    return currentIndex + 1;
};

/**
 * Filters sections to only template sections for backwards compatibility
 */
export const getTemplateSectionsOnly = (allSections: CombinedSection[]) => {
    return allSections
        .filter(section => section.sectionType === 'template')
        .map(section => ({
            appliedTemplateId: section.appliedTemplateId,
            section: section.section,
            isComplete: section.isComplete
        }));
};
